import React from 'react';
import { Collapse } from 'react-bootstrap';
import { i18n } from '../../i18n/translate';
import { formatClassName } from '../../utils';
import { FAIcon } from '../fa-icon';
import { ChakraUIProvider } from '../../react-on-rails/ChakraUIProvider';

const createMenuToggle = ({ className = '', ...props }) => {
  return (
    <button type="button" className={`custom-burger navbar-toggle collapsed d-md-none ${className}`} {...props}>
      <span className="sr-only">{i18n.t('Toggle navigation')}</span>
      <FAIcon icon="bars" />
    </button>
  );
};

export const createLearnerHeader = ({
  content,
  currentUrl,
  hasColors,
  isDashiFi,
  isImpersonating,
  isLearner,
  isMobile,
  isMobileMenuOpen,
  isDeactivatedOrg,
  leftMenu,
  menu,
  navbarRef,
  program,
  renderImpersonationBanner,
  renderDeactivatedOrgBanner,
  rightMenu,
  setMobileMenuOpen,
  theme,
}) => {
  const getContentLibrarySearchUrl = () => {
    const programSlug = program?.slug;
    const learnerDashboardPath = '/student/dashboard';
    const personalizedDashboardPath = '/student/personalized_dashboard';
    const trueLearnerDashboardPath = '/student/learner_dashboard';

    if (
      ![learnerDashboardPath, personalizedDashboardPath, trueLearnerDashboardPath].some(path =>
        currentUrl?.includes(path)
      ) ||
      !programSlug
    ) {
      return null;
    }

    const url = currentUrl.replace(personalizedDashboardPath, learnerDashboardPath, trueLearnerDashboardPath);
    return `${url.slice(0, url.indexOf(learnerDashboardPath))}/student/dashboard/${programSlug}/content_library`;
  };

  const className = formatClassName([
    'clearfix custom-header d-md-flex flex-md-column',
    { impersonating: isImpersonating },
    { deactivated: isDeactivatedOrg },
    { 'border-branded': isLearner() },
  ]);
  const navbarClassNames = formatClassName([
    'custom-content-navbar align-items-md-center d-md-flex',
    { 'navbar-collapse': isMobile },
    { 'navbar-hide': !isDashiFi && !hasColors },
  ]);
  const foundryMenuClassNames = formatClassName([
    'header-item foundry-menu float-md-end',
    { 'd-flex align-items-center': !!content },
  ]);

  const navbarItems = [
    leftMenu({ key: 'leftMenu' }),
    !!navbarRef.current?.childElementCount &&
      createMenuToggle({
        className: 'bg-transparent mt-0',
        'aria-expanded': String(isMobileMenuOpen),
        'aria-controls': 'navbar',
        onClick: () => setMobileMenuOpen(open => !open),
        key: 'menuToggle',
      }),
  ];

  const getContext = () => {
    let context = '';
    if (currentUrl) {
      const urlObject = new URL(currentUrl);
      if (urlObject.hostname.includes('compliance')) {
        context = 'compliance';
      }
    }
    return context;
  };

  return (
    <ChakraUIProvider theme={theme}>
      <div className={`foundry ${getContext()} learner-header`}>
        <header id="header" className={className} data-ma-theme="blue" data-testid="header">
          {isImpersonating && renderImpersonationBanner()}
          {isDeactivatedOrg && renderDeactivatedOrgBanner()}
          <div className="foundry-header-container d-md-flex">
            <div className="d-flex justify-content-between">{navbarItems}</div>
            <div className={foundryMenuClassNames}>
              <Collapse in={isMobileMenuOpen}>
                <div
                  id="navbar"
                  className={navbarClassNames}
                  aria-expanded={`${isMobileMenuOpen}`}
                  ref={navbarRef}
                  data-attribute="navbar"
                >
                  {rightMenu({ contentLibrarySearch: getContentLibrarySearchUrl() })}
                </div>
              </Collapse>
              {menu}
            </div>
          </div>
        </header>
      </div>
    </ChakraUIProvider>
  );
};

export const createDefaultHeader = ({
  content,
  customRight,
  isCustom,
  isImpersonating,
  isLearner,
  isMobile,
  isEverfiAdmin,
  isNextAdmin,
  isDeactivatedOrg,
  leftMenu,
  menu,
  renderImpersonationBanner,
  renderDeactivatedOrgBanner,
  rightMenu,
  theme,
}) => {
  const className = formatClassName([
    'clearfix',
    { impersonating: isImpersonating },
    { deactivated: isDeactivatedOrg },
    { 'border-branded': isLearner() },
    { 'custom-header': isCustom },
  ]);

  const containerClassName = formatClassName(['foundry-header-container', { fit: isEverfiAdmin }]);

  return (
    <ChakraUIProvider theme={theme}>
      <div className="foundry">
        <header id="header" className={className} data-ma-theme="blue" data-testid="header">
          {isImpersonating && renderImpersonationBanner()}
          {isDeactivatedOrg && renderDeactivatedOrgBanner()}
          <div className={containerClassName}>
            {leftMenu()}
            {isNextAdmin && (
              <div className="header-item navbar-search" data-testid="navbar-search">
                <form
                  role="search"
                  className="navbar-form-custom"
                  action="/admin/search"
                  acceptCharset="UTF-8"
                  method="get"
                >
                  <input name="utf8" type="hidden" value="✓" autoComplete="off" />
                  <div className="form-group">
                    <FAIcon icon="magnifying-glass" />
                    <input
                      type="text"
                      name="terms"
                      id="terms"
                      placeholder="Search..."
                      className="form-control top-search-input"
                      autoComplete="off"
                    />
                  </div>
                </form>
              </div>
            )}

            {!!customRight &&
              createMenuToggle({
                'data-toggle': 'collapse',
                'data-target': '#navbar',
                'aria-expanded': 'false',
                'aria-controls': 'navbar',
              })}

            <div className="header-item foundry-menu">
              {!!customRight && isMobile && (
                <div id="navbar" className="navbar-collapse custom-content-navbar" aria-expanded="false">
                  {rightMenu()}
                </div>
              )}
              {!!customRight && !isMobile && (
                <div id="navbar" className="customn-content-navbar">
                  {rightMenu()}
                </div>
              )}
              {!customRight && !content && rightMenu()}
              {menu}
            </div>
          </div>
        </header>
      </div>
    </ChakraUIProvider>
  );
};

/**
 * Checks if an object with no properties
 * @param {*} object
 * @returns {boolean}
 */
export const isObjectWithNoProperties = object =>
  typeof object === 'object' && object !== null && Object.keys(object).length === 0;
